import React, { useEffect, useState } from "react";
import CheckboxInput from "@/app/(components)/generics/inputs/default/CheckboxInput/CheckboxInput";
import { Input, InputProps } from "@/app/(components)/generics/inputs/default/Input/Input";
import inputStyles from "@/app/(components)/generics/inputs/default/Input/Input.module.css";
import defaultInputStyles from "@/app/(components)/generics/inputs/styles/DefaultInput.module.css";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./ChoiceInput.module.css";

type Props = {
  lang?: string;
  label?: string;
  labelSuffix?: React.ReactNode;
  isRequired?: boolean;
  showRequiredLabel?: boolean;
  data: any;
  dataKey?: string;
  options: {
    id: number;
    text: string;
    input?: InputProps;
  }[];
  otherOption?: {
    dataKey: string;
    text: string;
    input?: InputProps;
  };
  multipleChoice?: boolean;
  required?: {
    visible?: boolean;
    text: string;
  };
};

export default function ChoiceInput(props: Props) {
  const {
    lang,
    label,
    labelSuffix,
    isRequired,
    showRequiredLabel,
    data,
    dataKey,
    options,
    otherOption,
    multipleChoice,
    required,
  } = props;

  const [initiated, setInitiated] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<{ [id: number]: boolean }>({});
  const [otherOptionSelected, setOtherOptionSelected] = useState(false);

  useEffect(() => {
    if (initiated || !dataKey) return;

    const initialSelected: { [id: number]: boolean } = {};

    let found = false;
    options.forEach((opt) => {
      if (data[dataKey].includes(opt.text)) {
        initialSelected[opt.id] = true;
        found = true;
      }
    });

    setSelectedOptions(initialSelected);

    if (!found && otherOption) {
      setOtherOptionSelected(data[otherOption.dataKey]?.length > 0);
    }

    setInitiated(true);
  }, [data, dataKey, options, otherOption, initiated]);

  const handleSelect = (id: number, checked: boolean) => {
    if (!dataKey) return;

    if (multipleChoice) {
      data[dataKey] = [];
      setSelectedOptions((prev) => ({
        ...prev,
        [id]: checked,
      }));
    } else {
      data[dataKey] = "";
      options.forEach((option) => {
        if (option.input?.dataKey) {
          option.input.data[option.input.dataKey] = "";
        }
      });

      if (otherOption) {
        data[otherOption.dataKey] = "";
        setSelectedOptions({});
      }

      setSelectedOptions({ [id]: checked });
      setOtherOptionSelected(false);
    }
  };

  useEffect(() => {
    if (!initiated || !dataKey) return;

    if (multipleChoice) {
      data[dataKey] = options.filter((opt) => selectedOptions[opt.id]).map((opt) => opt.text);
    } else {
      options.forEach((opt) => {
        if (selectedOptions[opt.id]) data[dataKey] = opt.text;
      });
    }
  }, [selectedOptions, options, dataKey, multipleChoice, initiated, data]);

  const hasSelected = Object.values(selectedOptions).some(Boolean) || otherOptionSelected;

  return (
    <div className={styles.container}>
      {label && (
        <label className={inputStyles.label}>
          {label}
          {showRequiredLabel &&
            (isRequired ? (
              <span className={inputStyles.labelRequired}>*</span>
            ) : (
              <span className={inputStyles.labelOptional}>({T(TKey.OPTIONAL, lang)})</span>
            ))}
          {labelSuffix && <span className={inputStyles.labelSuffix}>{labelSuffix}</span>}
        </label>
      )}
      <div className={styles.options}>
        {options.map((option) => (
          <div className={styles.option} key={`choice-input${dataKey}-${option.id}`}>
            <div className={styles.optionTop}>
              <CheckboxInput
                label={option.text}
                checked={selectedOptions[option.id]}
                onToggle={(checked) => handleSelect(option.id, checked)}
                radio={!multipleChoice}
              />

              {option.input && selectedOptions[option.id] && <Input {...option.input} />}
            </div>
          </div>
        ))}

        {otherOption && (
          <div className={styles.option}>
            <div className={styles.optionTop}>
              <CheckboxInput
                label={otherOption.text}
                checked={otherOptionSelected}
                onToggle={(checked) => {
                  if (!multipleChoice && dataKey) {
                    data[dataKey] = "";
                    setSelectedOptions({});
                  }
                  setOtherOptionSelected(checked);
                }}
                radio={!multipleChoice}
              />
            </div>

            {otherOptionSelected && otherOption.input && <Input {...otherOption.input} />}
          </div>
        )}
      </div>
      {required?.visible && !hasSelected && (
        <div className={UStyles.classNames(defaultInputStyles.errorMessage)}>
          <ErrorMessage message={required.text} />
        </div>
      )}
    </div>
  );
}
